/**
 * The one place an editorial route is built. Every link is absolute, from the title's base
 * (`/editorial/<section.path>`), so it lands in the same place from whichever route renders it:
 * a relative `pieces/7` inside the ideas route would resolve to `/editorial/fj/ideas/pieces/7`.
 * `path` is `section.path` from `useEditorialTitle()`.
 */
export type LinkAction = 'review' | 'check_sources' | 'try_again' | 'publish_public_copy'

const enc = (path: string) => encodeURIComponent(path)
const q = (params: Record<string, string>): string => new URLSearchParams(params).toString()

/** The title's Desk, and the base every other link starts from. */
export const titleBase = (path: string): string => `/editorial/${enc(path)}`
export const deskHref = titleBase
/** The user manual, optionally at one section (ManualPage.tsx CONTENTS ids). */
export const manualHref = (section?: string): string => `/editorial/help${section ? `#${section}` : ''}`
export const ideasHref = (path: string): string => `${titleBase(path)}/ideas`
/** The Pieces list, optionally on one stage tab. */
export const piecesHref = (path: string, stage?: string): string =>
  `${titleBase(path)}/pieces${stage ? `?${q({ stage })}` : ''}`
/** Pieces a writer sent to an editor (the "Sent to editor" filter). */
export const sentPiecesHref = (path: string): string => `${titleBase(path)}/pieces?${q({ sent: '1' })}`
export const pieceHref = (path: string, id: number): string => `${titleBase(path)}/pieces/${id}`
export const sourcesHref = (path: string, id: number): string => `${titleBase(path)}/pieces/${id}/sources`
export const scheduleHref = (path: string): string => `${titleBase(path)}/schedule`
export const liveHref = (path: string, where?: string): string =>
  `${titleBase(path)}/live${where && where !== 'free' ? `?${q({ where })}` : ''}`
export const socialHref = (path: string, show?: string): string =>
  `${titleBase(path)}/social${show && show !== 'to_approve' ? `?${q({ show })}` : ''}`
export const topicsHref = (path: string): string => `${titleBase(path)}/topics`
export const settingsHref = (path: string): string => `${titleBase(path)}/settings`
/** A blank Start screen. */
export const startHref = (path: string): string => `${titleBase(path)}/start`
/** Draft this subject again: the Start screen filled from a piece. */
export const startFrom = (path: string, id: number): string => `${startHref(path)}?${q({ from: String(id) })}`
/** A ranked idea's Start link: Start loads the idea's full topic by its label. */
export const startIdea = (path: string, label: string): string => `${startHref(path)}?${q({ idea: label })}`

/** The editor's own idea: start?topic=&ask= */
export function startLink(path: string, idea: { label: string; ask?: string | null }): string {
  const p = new URLSearchParams()
  p.set('topic', idea.label.trim())
  const ask = (idea.ask ?? '').trim()
  if (ask) p.set('ask', ask)
  return `${startHref(path)}?${p.toString()}`
}

export function actionHref(path: string, action: LinkAction, id: number): string {
  switch (action) {
    case 'check_sources': return sourcesHref(path, id)
    case 'try_again': return startFrom(path, id)
    case 'review':
    case 'publish_public_copy':
      return pieceHref(path, id)
  }
}

/** An archive article's page: admins only (the archive is not part of the editorial area). */
export const archiveArticleHref = (id: number): string => `/lab/archive/${id}`
/** Make a version of a draft or an archive article. */
export const versionHref = (path: string, src: { draftId?: number; articleId?: number }): string =>
  `${titleBase(path)}/version?${q(src.draftId != null ? { draft: String(src.draftId) } : { article: String(src.articleId) })}`
/** The Import tab, and one import's page. */
export const importsHref = (path: string): string => `${titleBase(path)}/imports`
export const importHref = (path: string, id: number): string => `${importsHref(path)}/${id}`
